import type {
  FlowEdge,
  FlowGraph,
  FlowMetadata,
  FlowNode,
  NodeValidationError,
} from '@cafe/shared';
import { validateNodeData } from '@cafe/shared';
import { FlowTranspiler } from '@cafe/transpiler';
import {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  type Connection,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
} from '@xyflow/react';
import { temporal } from 'zundo';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import type { AutomationTrace } from '@/lib/ha-api';
import { getHomeAssistantAPI } from '@/lib/ha-api';
import { generateNodeId, generateUUID } from '@/lib/utils';
import type { HomeAssistant } from '@/types/hass';
import { cafeIndexedDBStorage } from '@/utils/indexeddb-storage';

/**
 * Node data types for React Flow
 */

export interface TriggerNodeData {
  alias?: string;
  trigger: string;
  entity_id?: string | string[];
  to?: string;
  from?: string;
  event_type?: string;
  [key: string]: unknown;
}

export interface ConditionNodeData {
  alias?: string;
  condition: string;
  entity_id?: string | string[];
  state?: string;
  template?: string;

  // Numeric state conditions
  above?: number;
  below?: number;

  // Time conditions
  after?: string;
  before?: string;
  weekday?: string | string[];

  // Zone conditions
  zone?: string;

  // Sun conditions
  after_offset?: string;
  before_offset?: string;

  // Device conditions
  device_id?: string;
  domain?: string;
  type?: string;
  subtype?: string;

  // Template conditions
  value_template?: string;

  // Generic conditions
  attribute?: string;
  for?: string | { hours?: number; minutes?: number; seconds?: number };

  [key: string]: unknown;
}

export interface ActionNodeData {
  alias?: string;
  service?: string;
  event?: string;
  event_data?: Record<string, unknown>;
  target?: {
    entity_id?: string | string[];
    area_id?: string | string[];
    device_id?: string | string[];
  };
  data?: Record<string, unknown>;
  // Stop action: halts automation execution with an optional message
  stop?: string;
  error?: boolean;
  [key: string]: unknown;
}

export interface DelayNodeData {
  alias?: string;
  delay: string | { hours?: number; minutes?: number; seconds?: number };
  [key: string]: unknown;
}

export interface WaitNodeData {
  alias?: string;
  wait_template?: string;
  wait_for_trigger?: TriggerNodeData[];
  timeout?: string;
  continue_on_timeout?: boolean;
  [key: string]: unknown;
}

export interface SetVariablesNodeData {
  alias?: string;
  variables: Record<string, unknown>;
  [key: string]: unknown;
}

export type FlowNodeData =
  | TriggerNodeData
  | ConditionNodeData
  | ActionNodeData
  | DelayNodeData
  | WaitNodeData
  | SetVariablesNodeData;

/**
 * Flow store state
 */
export interface FlowState {
  // Graph state
  flowId: string;
  flowName: string;
  flowDescription: string;
  nodes: Node<FlowNodeData>[];
  edges: Edge[];

  // Automation metadata (mode, max, max_exceeded, etc.)
  flowMetadata: FlowMetadata;

  // User-defined root-level variables (preserved across import/export round-trips)
  userVariables: Record<string, unknown> | undefined;

  // User-defined trigger_variables (preserved across import/export round-trips)
  userTriggerVariables: Record<string, unknown> | undefined;

  // Selection state
  selectedNodeId: string | null;
  rightPanelExpanded: boolean;

  // Save state
  automationId: string | null;
  isSaving: boolean;
  lastSaved: Date | null;
  hasUnsavedChanges: boolean;
  originalSnapshot: string | null; // JSON snapshot of original state for comparison

  // Simulation state
  isSimulating: boolean;
  activeNodeId: string | null;
  executionPath: string[];

  // Trace state
  isShowingTrace: boolean;
  traceData: AutomationTrace | null;
  traceExecutionPath: string[];
  traceTimestamps: Record<string, string>;

  // Shared simulation/trace state
  simulationSpeed: number;

  // Toolbar state
  clipboard: string | null;
  pasteCount: number;

  // Actions
  setNodes: (nodes: Node<FlowNodeData>[]) => void;
  setEdges: (edges: Edge[]) => void;
  onNodesChange: (changes: NodeChange<Node<FlowNodeData>>[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  onConnect: (connection: Connection) => void;

  addNode: (node: Node<FlowNodeData>) => void;
  updateNodeData: (nodeId: string, data: Partial<FlowNodeData>) => void;
  removeNode: (nodeId: string) => void;
  addWaitTimeoutBranch: (waitNodeId: string) => void;

  selectNode: (nodeId: string | null) => void;
  clearCanvasSelection: () => void;
  setRightPanelExpanded: (expanded: boolean) => void;
  toggleRightPanelExpanded: () => void;

  setFlowName: (name: string) => void;
  setFlowDescription: (description: string) => void;
  setFlowMetadata: (metadata: Partial<FlowMetadata>) => void;

  setClipboard: (data: string | null) => void;
  setPasteCount: (count: number) => void;

  // Save actions
  setAutomationId: (id: string | null) => void;
  setSaving: (saving: boolean) => void;
  setSaved: () => void;
  setUnsavedChanges: (hasChanges: boolean) => void;
  saveAutomation: (hassApi: HomeAssistant) => Promise<string>;
  updateAutomation: (hassApi: HomeAssistant) => Promise<void>;
  hasRealChanges: () => boolean; // Compare current state to original snapshot

  // Simulation
  startSimulation: () => void;
  stopSimulation: () => void;
  setActiveNode: (nodeId: string | null) => void;
  addToExecutionPath: (nodeId: string) => void;
  clearExecutionPath: () => void;

  // Trace
  showTrace: (traceData: AutomationTrace) => void;
  hideTrace: () => void;
  clearTraceExecutionPath: () => void;

  // Shared simulation/trace actions
  setSimulationSpeed: (speed: number) => void;
  getExecutionStepNumber: (nodeId: string) => number | null;

  // Edge validation
  canDeleteEdge: (edgeId: string) => boolean;

  // Import/Export
  toFlowGraph: () => FlowGraph;
  fromFlowGraph: (graph: FlowGraph) => void;
  reset: () => void;

  // Node validation
  nodeErrors: Map<string, NodeValidationError[]>;
  validateNode: (nodeId: string) => void;
  validateAllNodes: () => void;
  clearNodeErrors: (nodeId: string) => void;
  hasValidationErrors: () => boolean;
}

/**
 * Normalize trigger node data to use 'trigger' instead of legacy 'platform' field.
 * This ensures consistency across the codebase.
 */
function normalizeTriggerData(data: Record<string, unknown>): Record<string, unknown> {
  if ('platform' in data && !('trigger' in data)) {
    const { platform, ...rest } = data;
    return { ...rest, trigger: platform };
  }
  return data;
}

/**
 * Normalize node data based on node type.
 * Currently only normalizes trigger nodes.
 */
function normalizeNodeData(type: string, data: Record<string, unknown>): Record<string, unknown> {
  if (type === 'trigger') {
    return normalizeTriggerData(data);
  }
  return data;
}

/**
 * Finds nodes that share the same non-empty `data.id` (the exported HA step
 * `id:` — a separate field from the node's own graph id, see `updateNodeData`).
 * Two steps with the same id would either fail to load in HA or make
 * `trigger.id`/`choose:` routing ambiguous, and this can't be caught by
 * `validateNodeData`'s per-node schema check, which never sees the rest of the
 * graph.
 */
/**
 * Drop validation errors belonging to nodes that no longer exist.
 *
 * `nodeErrors` is keyed by node ID, so deleting a node would otherwise leave its
 * errors behind forever: the canvas reports a non-zero error count that no
 * visible node accounts for, and nothing can clear it because the node is gone.
 * Returns the same Map instance when nothing changed, so subscribers don't
 * re-render needlessly.
 */
function pruneNodeErrors(
  nodeErrors: Map<string, NodeValidationError[]>,
  nodes: Node<FlowNodeData>[]
): Map<string, NodeValidationError[]> {
  if (nodeErrors.size === 0) return nodeErrors;

  const liveIds = new Set(nodes.map((n) => n.id));
  const stale = [...nodeErrors.keys()].filter((id) => !liveIds.has(id));
  if (stale.length === 0) return nodeErrors;

  const pruned = new Map(nodeErrors);
  for (const id of stale) {
    pruned.delete(id);
  }
  return pruned;
}

/**
 * A short human-readable name for a node, for error messages that need to point
 * the user at a specific node on the canvas.
 */
function describeNode(node: Node<FlowNodeData>): string {
  const data = node.data as Record<string, unknown>;
  const candidates = [data.alias, data.service, data.event, data.trigger, data.condition];
  const label = candidates.find((v): v is string => typeof v === 'string' && v.trim() !== '');
  return label ? `${label} (${node.id})` : node.id;
}

/**
 * Throw a save-blocking error naming the invalid nodes, and select the first one
 * so the user can actually find it.
 *
 * "Fix the highlighted nodes" is useless on its own when the offending node sits
 * outside the viewport — the original report for this was a flow whose two bad
 * nodes were at x=3000 while everything else was under x=1185.
 */
function assertNodesAreValid(
  nodes: Node<FlowNodeData>[],
  nodeErrors: Map<string, NodeValidationError[]>,
  selectNode: (nodeId: string) => void
): void {
  if (nodeErrors.size === 0) return;

  const invalid = nodes.filter((n) => nodeErrors.has(n.id));
  const firstInvalid = invalid[0];
  if (firstInvalid) {
    selectNode(firstInvalid.id);
  }

  const names = invalid.map(describeNode);
  const shown = names.slice(0, 3).join(', ');
  const remaining = names.length > 3 ? `, and ${names.length - 3} more` : '';
  const named = names.length > 0 ? `: ${shown}${remaining}` : '';

  throw new Error(
    `Cannot save: ${nodeErrors.size} node(s) have validation errors${named}. ` +
      `They are highlighted in red — the first one is now selected, and "fit view" will bring it into sight if it is off-screen.`
  );
}

function findDuplicateIdErrors(nodes: Node<FlowNodeData>[]): Map<string, NodeValidationError> {
  const idToNodeIds = new Map<string, string[]>();
  for (const node of nodes) {
    const id = node.data.id;
    if (typeof id !== 'string' || id.trim() === '') continue;
    idToNodeIds.set(id, [...(idToNodeIds.get(id) ?? []), node.id]);
  }

  const errors = new Map<string, NodeValidationError>();
  for (const [, nodeIds] of idToNodeIds) {
    if (nodeIds.length < 2) continue;
    for (const nodeId of nodeIds) {
      errors.set(nodeId, { path: ['id'], message: 'errors:validation.node.duplicateId' });
    }
  }
  return errors;
}

const defaultFlowMetadata: FlowMetadata = {
  mode: 'single',
  initial_state: true,
};

const initialState = {
  flowId: generateUUID(),
  flowName: 'Untitled Automation',
  flowDescription: '',
  flowMetadata: defaultFlowMetadata,
  userVariables: undefined,
  userTriggerVariables: undefined,
  nodes: [],
  edges: [],
  selectedNodeId: null,
  rightPanelExpanded: false,
  automationId: null,
  isSaving: false,
  lastSaved: null,
  hasUnsavedChanges: false,
  originalSnapshot: null,
  isSimulating: false,
  activeNodeId: null,
  executionPath: [],
  isShowingTrace: false,
  traceData: null,
  traceExecutionPath: [],
  traceTimestamps: {},
  simulationSpeed: 800,
  nodeErrors: new Map<string, NodeValidationError[]>(),
  clipboard: null,
  pasteCount: 0,
};

/**
 * Persisted state for the flow store
 * This is duplicated here to avoid circular dependencies
 */
export type PersistedFlowState = Pick<
  FlowState,
  | 'flowId'
  | 'flowName'
  | 'flowDescription'
  | 'flowMetadata'
  | 'nodes'
  | 'edges'
  | 'selectedNodeId'
  | 'automationId'
  | 'lastSaved'
  | 'originalSnapshot'
>;

// Partial state selector for persistence
const persistSelector = (state: FlowState): PersistedFlowState => ({
  flowId: state.flowId,
  flowName: state.flowName,
  flowDescription: state.flowDescription,
  flowMetadata: state.flowMetadata,
  nodes: state.nodes,
  edges: state.edges,
  selectedNodeId: state.selectedNodeId,
  automationId: state.automationId,
  lastSaved: state.lastSaved,
  originalSnapshot: state.originalSnapshot,
});

/**
 * The subset of FlowState that undo/redo tracks — graph content only.
 * Selection, save/dirty bookkeeping, simulation/trace UI state, clipboard,
 * and derived validation errors are deliberately excluded so they don't
 * generate (or get reverted by) history entries.
 */
export type TemporalFlowState = Pick<
  FlowState,
  | 'nodes'
  | 'edges'
  | 'flowName'
  | 'flowDescription'
  | 'flowMetadata'
  | 'userVariables'
  | 'userTriggerVariables'
>;

const temporalSelector = (state: FlowState): TemporalFlowState => ({
  nodes: state.nodes,
  edges: state.edges,
  flowName: state.flowName,
  flowDescription: state.flowDescription,
  flowMetadata: state.flowMetadata,
  userVariables: state.userVariables,
  userTriggerVariables: state.userTriggerVariables,
});

let pendingHistoryCommit:
  | { timeoutId: ReturnType<typeof setTimeout>; burstStart: TemporalFlowState }
  | undefined;

/**
 * Cancels an in-flight debounced history commit (see `handleSet` below)
 * without flushing it. Called from `reset()`/`fromFlowGraph()` alongside
 * `temporal.getState().clear()` — clearing the past/future arrays alone
 * isn't enough, since a burst that was still debouncing when the graph got
 * replaced would otherwise fire *after* the clear and push a snapshot of the
 * *previous* automation into the *new* one's history.
 */
function cancelPendingHistoryCommit(): void {
  if (pendingHistoryCommit) {
    clearTimeout(pendingHistoryCommit.timeoutId);
    pendingHistoryCommit = undefined;
  }
}

export const useFlowStore = create<FlowState>()(
  persist(
    temporal(
      (set, get) => ({
        ...initialState,

        setNodes: (nodes) => set({ nodes }),
        setEdges: (edges) => set({ edges }),

        onNodesChange: (changes) =>
          set((state) => {
            const nodes = applyNodeChanges(changes, state.nodes);
            return {
              nodes,
              // Deleting a node here (e.g. the Delete key) must take its
              // validation errors with it
              nodeErrors: pruneNodeErrors(state.nodeErrors, nodes),
              hasUnsavedChanges: true,
            };
          }),

        onEdgesChange: (changes) =>
          set((state) => ({
            edges: applyEdgeChanges(changes, state.edges),
            hasUnsavedChanges: true,
          })),

        onConnect: (connection) =>
          set((state) => ({
            edges: addEdge(
              {
                ...connection,
                id: `e-${connection.source}-${connection.target}-${Date.now()}`,
                animated: false,
              },
              state.edges
            ),
            hasUnsavedChanges: true,
          })),

        addNode: (node) => {
          // Normalize node data (e.g., convert platform to trigger for trigger nodes)
          const normalizedNode = node.type
            ? {
                ...node,
                data: normalizeNodeData(
                  node.type,
                  node.data as Record<string, unknown>
                ) as FlowNodeData,
              }
            : node;

          set((state) => ({
            nodes: [...state.nodes, normalizedNode],
            hasUnsavedChanges: true,
          }));
          // Validate the newly added node
          get().validateNode(node.id);
        },

        updateNodeData: (nodeId, data) => {
          set((state) => ({
            nodes: state.nodes.map((node) =>
              node.id === nodeId ? { ...node, data: { ...node.data, ...data } } : node
            ),
            hasUnsavedChanges: true,
          }));
          if ('id' in data) {
            // A rename can resolve a duplicate on one node while creating a new
            // one on another — single-node validation can't see that.
            get().validateAllNodes();
          } else {
            // Validate the node after data update
            get().validateNode(nodeId);
          }
        },

        removeNode: (nodeId) =>
          set((state) => {
            const nodes = state.nodes.filter((n) => n.id !== nodeId);
            return {
              nodes,
              edges: state.edges.filter((e) => e.source !== nodeId && e.target !== nodeId),
              selectedNodeId: state.selectedNodeId === nodeId ? null : state.selectedNodeId,
              nodeErrors: pruneNodeErrors(state.nodeErrors, nodes),
              hasUnsavedChanges: true,
            };
          }),

        // Inserts a template Condition node right after a Wait node so the user
        // can branch on `wait.trigger is not none` / `wait.completed` — i.e.
        // "did it happen in time, or did it time out". Any edges already
        // leaving the Wait node are re-parented onto the condition's 'true'
        // (completed-in-time) handle so the existing continuation keeps
        // working; the 'false' (timed-out) handle is left for the user to
        // wire up themselves.
        addWaitTimeoutBranch: (waitNodeId) => {
          const state = get();
          const waitNode = state.nodes.find((n) => n.id === waitNodeId);
          if (!waitNode || waitNode.type !== 'wait') return;
          const waitData = waitNode.data as WaitNodeData;
          if (!waitData.timeout) return;

          const conditionId = generateNodeId('condition');
          const template =
            waitData.wait_for_trigger !== undefined
              ? '{{ wait.trigger is not none }}'
              : '{{ wait.completed }}';

          const conditionNode: Node<FlowNodeData> = {
            id: conditionId,
            type: 'condition',
            position: { x: waitNode.position.x + 260, y: waitNode.position.y },
            data: {
              alias: 'Wait completed?',
              condition: 'template',
              value_template: template,
            } satisfies ConditionNodeData,
          };

          set((s) => ({
            nodes: [
              ...s.nodes.map((n) =>
                n.id === waitNodeId ? { ...n, data: { ...n.data, continue_on_timeout: true } } : n
              ),
              conditionNode,
            ],
            edges: [
              ...s.edges.map((e) =>
                e.source === waitNodeId ? { ...e, source: conditionId, sourceHandle: 'true' } : e
              ),
              {
                id: `e-${waitNodeId}-${conditionId}-${Date.now()}`,
                source: waitNodeId,
                target: conditionId,
                animated: false,
              },
            ],
            hasUnsavedChanges: true,
          }));
          get().validateNode(conditionId);
        },

        selectNode: (nodeId) => set({ selectedNodeId: nodeId }),

        clearCanvasSelection: () =>
          set((state) => {
            const hasSelection =
              state.selectedNodeId !== null ||
              state.nodes.some((node) => node.selected) ||
              state.edges.some((edge) => edge.selected);

            if (!hasSelection) {
              return state;
            }

            return {
              selectedNodeId: null,
              nodes: state.nodes.map((node) => ({ ...node, selected: false })),
              edges: state.edges.map((edge) => ({ ...edge, selected: false })),
            };
          }),

        setRightPanelExpanded: (expanded) => set({ rightPanelExpanded: expanded }),

        toggleRightPanelExpanded: () =>
          set((state) => ({ rightPanelExpanded: !state.rightPanelExpanded })),

        setClipboard: (data: string | null) => set({ clipboard: data }),
        setPasteCount: (count: number) => set({ pasteCount: count }),

        setFlowName: (name) => set({ flowName: name, hasUnsavedChanges: true }),
        setFlowDescription: (description) =>
          set({ flowDescription: description, hasUnsavedChanges: true }),
        setFlowMetadata: (metadata) =>
          set((state) => ({
            flowMetadata: { ...state.flowMetadata, ...metadata },
            hasUnsavedChanges: true,
          })),

        // Save actions
        setAutomationId: (id) => set({ automationId: id }),
        setSaving: (saving) => set({ isSaving: saving }),
        setSaved: () => set({ lastSaved: new Date(), hasUnsavedChanges: false }),
        setUnsavedChanges: (hasChanges) => set({ hasUnsavedChanges: hasChanges }),
        hasRealChanges: () => {
          const state = get();
          if (!state.originalSnapshot) {
            // No original snapshot means it's a new flow - check if there are any nodes
            return state.nodes.length > 0;
          }
          // Create current snapshot and compare
          const currentSnapshot = JSON.stringify({
            flowName: state.flowName,
            flowDescription: state.flowDescription,
            flowMetadata: state.flowMetadata,
            nodes: state.nodes.map((n) => ({
              id: n.id,
              type: n.type,
              position: n.position,
              data: n.data,
            })),
            edges: state.edges.map((e) => ({
              id: e.id,
              source: e.source,
              target: e.target,
              sourceHandle: e.sourceHandle,
              targetHandle: e.targetHandle,
            })),
          });
          return currentSnapshot !== state.originalSnapshot;
        },

        saveAutomation: async (hassApi: HomeAssistant) => {
          const state = get();
          const api = getHomeAssistantAPI(hassApi);

          set({ isSaving: true });

          try {
            // Validate all nodes first
            get().validateAllNodes();

            // Check for validation errors
            const currentState = get();
            assertNodesAreValid(currentState.nodes, currentState.nodeErrors, (nodeId) =>
              set({ selectedNodeId: nodeId })
            );

            // Convert flow to graph
            const graph = state.toFlowGraph();

            // Check for empty automation
            if (graph.nodes.length === 0) {
              throw new Error(
                'Cannot save empty automation. Please add at least one trigger and one action node.'
              );
            }

            // Check for minimum required nodes
            const triggers = graph.nodes.filter((n) => n.type === 'trigger');
            const actions = graph.nodes.filter((n) => n.type === 'action');

            if (triggers.length === 0) {
              throw new Error(
                'Automation must have at least one trigger node. Please add a trigger from the node palette.'
              );
            }

            if (actions.length === 0) {
              throw new Error(
                'Automation must have at least one action node. Please add an action from the node palette.'
              );
            }

            const transpiler = new FlowTranspiler();

            // Validate first
            const validation = transpiler.validate(graph);

            if (validation.errors.length > 0) {
              console.error('C.A.F.E.: Validation errors:', validation.errors);
              throw new Error(
                `Validation failed: ${validation.errors.map((e) => e.message).join(', ')}`
              );
            }

            // Transpile to automation config
            const result = transpiler.transpile(graph);
            if (!result.success || !result.output?.automation) {
              throw new Error('Failed to transpile flow to automation config');
            }

            // Create automation in Home Assistant
            const automationConfig = {
              alias: state.flowName,
              description: state.flowDescription || '',
              ...result.output.automation,
              variables: {
                ...(result.output.automation.variables || {}),
                _cafe_metadata: {
                  version: 1,
                  strategy: 'native' as const,
                  nodes: graph.nodes.reduce(
                    (acc, node) => {
                      acc[node.id] = {
                        x: node.position.x,
                        y: node.position.y,
                      };
                      return acc;
                    },
                    {} as Record<string, { x: number; y: number }>
                  ),
                  graph_id: graph.id,
                  graph_version: 1,
                },
              },
            };

            const automationId = await api.createAutomation(automationConfig);

            set({
              automationId,
              isSaving: false,
              lastSaved: new Date(),
              hasUnsavedChanges: false,
            });

            return automationId;
          } catch (error) {
            set({ isSaving: false });
            throw error;
          }
        },

        updateAutomation: async (hassApi: HomeAssistant) => {
          const state = get();
          const api = getHomeAssistantAPI(hassApi);

          if (!state.automationId) {
            throw new Error('No automation ID set. Use saveAutomation() for new automations.');
          }

          console.log('C.A.F.E.: Updating automation with ID from store:', state.automationId);

          set({ isSaving: true });

          try {
            // Validate all nodes first
            get().validateAllNodes();

            // Check for validation errors
            const currentState = get();
            assertNodesAreValid(currentState.nodes, currentState.nodeErrors, (nodeId) =>
              set({ selectedNodeId: nodeId })
            );

            // Convert flow to graph
            const graph = state.toFlowGraph();

            // Check for empty automation
            if (graph.nodes.length === 0) {
              throw new Error(
                'Cannot save empty automation. Please add at least one trigger and one action node.'
              );
            }

            // Check for minimum required nodes
            const triggers = graph.nodes.filter((n) => n.type === 'trigger');
            const actions = graph.nodes.filter((n) => n.type === 'action');

            if (triggers.length === 0) {
              throw new Error(
                'Automation must have at least one trigger node. Please add a trigger from the node palette.'
              );
            }

            if (actions.length === 0) {
              throw new Error(
                'Automation must have at least one action node. Please add an action from the node palette.'
              );
            }

            const transpiler = new FlowTranspiler();

            // Validate first
            const validation = transpiler.validate(graph);
            if (validation.errors.length > 0) {
              throw new Error(
                `Validation failed: ${validation.errors.map((e) => e.message).join(', ')}`
              );
            }

            // Transpile to automation config
            const result = transpiler.transpile(graph);
            if (!result.success || !result.output?.automation) {
              throw new Error('Failed to transpile flow to automation config');
            }

            // Update automation in Home Assistant
            const automationConfig = {
              alias: state.flowName,
              description: state.flowDescription || '',
              ...result.output.automation,
              variables: {
                ...(result.output.automation.variables || {}),
                _cafe_metadata: {
                  version: 1,
                  strategy: 'native' as const,
                  nodes: graph.nodes.reduce(
                    (acc, node) => {
                      acc[node.id] = {
                        x: node.position.x,
                        y: node.position.y,
                      };
                      return acc;
                    },
                    {} as Record<string, { x: number; y: number }>
                  ),
                  graph_id: graph.id,
                  graph_version: 1,
                },
              },
            };

            await api.updateAutomation(state.automationId, automationConfig);

            set({
              isSaving: false,
              lastSaved: new Date(),
              hasUnsavedChanges: false,
            });
          } catch (error) {
            set({ isSaving: false });
            throw error;
          }
        },

        startSimulation: () => set({ isSimulating: true, executionPath: [], activeNodeId: null }),
        stopSimulation: () => set({ isSimulating: false, activeNodeId: null }),
        setActiveNode: (nodeId) => set({ activeNodeId: nodeId }),
        addToExecutionPath: (nodeId) =>
          set((state) => ({
            executionPath: [...state.executionPath, nodeId],
          })),
        clearExecutionPath: () => set({ executionPath: [] }),

        showTrace: (traceData) => {
          const traceExecutionPath: string[] = [];
          const traceTimestamps: Record<string, string> = {};

          // Extract execution path and timestamps from trace data
          if (traceData?.trace) {
            // Get current flow nodes grouped by type and sorted by position
            const state = get();
            const nodesByType: Record<string, Node<FlowNodeData>[]> = {
              trigger: [],
              condition: [],
              action: [],
              wait: [],
              delay: [],
            };

            // Group nodes by type and sort them (could be by y-position or order in array)
            for (const node of state.nodes) {
              const nodeType = node.type as keyof typeof nodesByType;
              if (nodesByType[nodeType]) {
                nodesByType[nodeType].push(node);
              }
            }

            // Sort each group by y-position to match likely execution order
            for (const type in nodesByType) {
              nodesByType[type].sort((a, b) => a.position.y - b.position.y);
            }

            // Sort trace steps by timestamp to get execution order
            const sortedSteps = Object.entries(traceData.trace)
              .flatMap(([path, steps]) => {
                if (Array.isArray(steps)) {
                  return steps.map((step) => ({ ...step, path }));
                }
                return [];
              })
              .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

            // Map trace paths to actual node IDs
            for (const step of sortedSteps) {
              const pathParts = step.path.split('/');
              const nodeType = pathParts[0]; // trigger, condition, action, etc.
              const nodeIndex = parseInt(pathParts[1], 10); // 0, 1, 2, etc.

              // Find the corresponding node in our flow
              const nodesOfType = nodesByType[nodeType] || [];
              if (nodesOfType[nodeIndex]) {
                const nodeId = nodesOfType[nodeIndex].id;

                if (!traceExecutionPath.includes(nodeId)) {
                  traceExecutionPath.push(nodeId);
                  traceTimestamps[nodeId] = step.timestamp;
                }
              }
            }
          }

          set({
            isShowingTrace: true,
            traceData,
            traceExecutionPath,
            traceTimestamps,
            activeNodeId: null,
          });
        },
        hideTrace: () =>
          set({
            isShowingTrace: false,
            traceData: null,
            traceExecutionPath: [],
            traceTimestamps: {},
            activeNodeId: null,
          }),
        clearTraceExecutionPath: () => set({ traceExecutionPath: [], traceTimestamps: {} }),

        setSimulationSpeed: (speed) => set({ simulationSpeed: speed }),
        getExecutionStepNumber: (nodeId) => {
          const state = get();
          // Check simulation execution path first
          if (state.isSimulating && state.executionPath.length > 0) {
            const stepIndex = state.executionPath.indexOf(nodeId);
            return stepIndex >= 0 ? stepIndex + 1 : null;
          }
          // Check trace execution path
          if (state.isShowingTrace && state.traceExecutionPath.length > 0) {
            const stepIndex = state.traceExecutionPath.indexOf(nodeId);
            return stepIndex >= 0 ? stepIndex + 1 : null;
          }
          return null;
        },

        canDeleteEdge: () => {
          // All edges are always deletable
          return true;
        },

        toFlowGraph: (): FlowGraph => {
          const state = get();
          const nodeIds = new Set(state.nodes.map((n) => n.id));

          return {
            id: state.flowId,
            name: state.flowName,
            description: state.flowDescription || undefined,
            nodes: state.nodes.map((n) => {
              // Ensure node has all required fields
              const nodeData = { ...n.data };

              // Add missing required fields for different node types
              if (n.type === 'trigger' && !nodeData.trigger) {
                console.warn(
                  `C.A.F.E.: Trigger node ${n.id} missing trigger type, adding default 'state'`
                );
                nodeData.trigger = 'state';
              }

              if (n.type === 'action' && !nodeData.service) {
                console.warn(
                  `C.A.F.E.: Action node ${n.id} missing service, adding default 'light.turn_on'`
                );
                nodeData.service = 'light.turn_on';
              }

              return {
                id: n.id,
                type: n.type as FlowNode['type'],
                position: n.position,
                data: nodeData as FlowNode['data'],
              };
            }) as FlowNode[],
            // Filter out orphaned edges that reference deleted nodes
            edges: state.edges
              .filter((e) => nodeIds.has(e.source) && nodeIds.has(e.target))
              .map((e) => ({
                id: e.id,
                source: e.source,
                target: e.target,
                sourceHandle: e.sourceHandle,
                targetHandle: e.targetHandle,
                label: typeof e.label === 'string' ? e.label : undefined,
              })) as FlowEdge[],
            metadata: state.flowMetadata,
            version: 1,
            userVariables: state.userVariables,
            userTriggerVariables: state.userTriggerVariables,
          };
        },

        fromFlowGraph: (graph) => {
          const nodes = graph.nodes.map((n) => ({
            id: n.id,
            type: n.type,
            position: n.position,
            // Normalize node data when loading (e.g., convert platform to trigger)
            data: normalizeNodeData(n.type, n.data as Record<string, unknown>) as FlowNodeData,
          }));
          const edges = graph.edges.map((e) => ({
            id: e.id,
            source: e.source,
            target: e.target,
            sourceHandle: e.sourceHandle,
            targetHandle: e.targetHandle,
            label: e.label,
          }));
          const importedMetadata: FlowMetadata = {
            ...defaultFlowMetadata,
            ...graph.metadata,
          };
          // Create snapshot for comparison
          const originalSnapshot = JSON.stringify({
            flowName: graph.name,
            flowDescription: graph.description || '',
            flowMetadata: importedMetadata,
            nodes: nodes.map((n) => ({
              id: n.id,
              type: n.type,
              position: n.position,
              data: n.data,
            })),
            edges: edges.map((e) => ({
              id: e.id,
              source: e.source,
              target: e.target,
              sourceHandle: e.sourceHandle,
              targetHandle: e.targetHandle,
            })),
          });
          set({
            flowId: graph.id,
            flowName: graph.name,
            flowDescription: graph.description || '',
            flowMetadata: importedMetadata,
            userVariables: graph.userVariables,
            userTriggerVariables: graph.userTriggerVariables,
            nodes,
            edges,
            selectedNodeId: null,
            // Reset save state when importing
            automationId: null,
            hasUnsavedChanges: false,
            lastSaved: null,
            originalSnapshot,
            nodeErrors: new Map(),
          });
          // Validate all nodes after loading
          get().validateAllNodes();
          // A freshly loaded automation shouldn't offer undo back into
          // whatever was open before it.
          cancelPendingHistoryCommit();
          useFlowStore.temporal.getState().clear();
        },

        reset: () => {
          set({
            ...initialState,
            flowId: generateUUID(),
            flowMetadata: { ...defaultFlowMetadata },
            originalSnapshot: null,
            nodeErrors: new Map(),
          });
          cancelPendingHistoryCommit();
          useFlowStore.temporal.getState().clear();
        },

        // Node validation
        validateNode: (nodeId) => {
          const state = get();
          const node = state.nodes.find((n) => n.id === nodeId);
          if (!node || !node.type) return;

          const errors = validateNodeData(node.type, node.data as Record<string, unknown>);

          set((s) => {
            const newErrors = new Map(s.nodeErrors);
            if (errors.length > 0) {
              newErrors.set(nodeId, errors);
            } else {
              newErrors.delete(nodeId);
            }
            return { nodeErrors: newErrors };
          });
        },

        validateAllNodes: () => {
          const state = get();
          const newErrors = new Map<string, NodeValidationError[]>();

          for (const node of state.nodes) {
            if (!node.type) continue;
            const errors = validateNodeData(node.type, node.data as Record<string, unknown>);
            if (errors.length > 0) {
              newErrors.set(node.id, errors);
            }
          }

          const duplicateIdErrors = findDuplicateIdErrors(state.nodes);
          for (const [nodeId, error] of duplicateIdErrors) {
            newErrors.set(nodeId, [...(newErrors.get(nodeId) ?? []), error]);
          }

          set({ nodeErrors: newErrors });
        },

        clearNodeErrors: (nodeId) => {
          set((s) => {
            const newErrors = new Map(s.nodeErrors);
            newErrors.delete(nodeId);
            return { nodeErrors: newErrors };
          });
        },

        hasValidationErrors: () => {
          return get().nodeErrors.size > 0;
        },
      }),
      {
        partialize: temporalSelector,
        limit: 100,
        // Without this, zundo pushes a history entry on *every* set() call
        // regardless of whether the tracked (partialized) fields actually
        // changed — so UI-only actions like selectNode/setClipboard, which
        // never touch nodes/edges/flowName/etc., would still create no-op
        // undo steps. Shallow-compares the partialized fields; nodes/edges
        // get new array references on every real structural change, so this
        // only skips truly no-op sets.
        equality: shallow,
        // Coalesces bursts of rapid changes (every pointer-move frame of a
        // node drag, a run of keystrokes, an add-then-immediately-edit) into
        // ONE history entry spanning the whole burst, not just its last
        // step. A plain trailing-edge debounce would only keep the *last*
        // call's `pastState` — the state just before that final call —
        // silently discarding the earlier steps' undo information, so
        // undoing would only jump back one micro-step instead of to before
        // the burst started. This remembers the *first* call's `pastState`
        // for the duration of the burst and only actually commits 300ms
        // after it goes quiet. Only the trigger for committing a snapshot is
        // delayed; the live store state itself updates immediately.
        handleSet: (handleSet) => (pastStateArg: FlowState | ((state: FlowState) => FlowState)) => {
          // zundo's own .d.ts mistypes this parameter as `FlowState |
          // ((state: FlowState) => FlowState)` — an artifact of `Parameters<>`
          // only resolving the *last* overload of zustand's own overloaded
          // `setState`. Verified against zundo/dist/index.js's
          // `temporalHandleSet`: at runtime this is always
          // `options.partialize(get())`, i.e. our own `temporalSelector`
          // output — a genuine `TemporalFlowState`.
          const pastState = pastStateArg as unknown as TemporalFlowState;
          const burstStart = pendingHistoryCommit?.burstStart ?? pastState;
          cancelPendingHistoryCommit();
          const timeoutId = setTimeout(() => {
            pendingHistoryCommit = undefined;
            handleSet(burstStart);
          }, 300);
          pendingHistoryCommit = { timeoutId, burstStart };
        },
      }
    ),
    {
      name: 'cafe-flow-storage',
      storage: cafeIndexedDBStorage,
      partialize: persistSelector,
      version: 1,
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Normalize node data after rehydration (e.g., convert platform to trigger)
          const normalizedNodes = state.nodes.map((n) => ({
            ...n,
            data: n.type
              ? (normalizeNodeData(n.type, n.data as Record<string, unknown>) as FlowNodeData)
              : n.data,
          }));

          // Update nodes if any were normalized
          const hasChanges = normalizedNodes.some(
            (n, i) => JSON.stringify(n.data) !== JSON.stringify(state.nodes[i].data)
          );
          if (hasChanges) {
            state.nodes = normalizedNodes;
          }

          // Validate all nodes after normalization
          state.validateAllNodes();
        }
      },
    }
  )
);
